import BlockRenderer from '../BlockRenderer'
import { useDroppable } from '@dnd-kit/core'
import { useBuilder } from '../../context/BuilderContext'
import { Trash2 } from 'lucide-react'

function DroppableColumn({ columnId, column, columnIndex, parentBlockId }) {
  const { setNodeRef, isOver } = useDroppable({
    id: columnId,
    data: {
      type: 'column',
      columnIndex,
      parentBlockId
    }
  })
  const { selectedBlockId, setSelectedBlockId, setBlocks } = useBuilder()

  const handleDeleteNestedBlock = (blockId, e) => {
    e.stopPropagation()
    if (!confirm('Delete this block?')) return

    setBlocks(prev => prev.map(block => {
      if (block.id === parentBlockId && block.type === 'columns') {
        const updatedColumns = block.settings.columns.map((col, idx) => {
          if (idx === columnIndex) {
            return {
              ...col,
              blocks: (col.blocks || []).filter(b => b.id !== blockId)
            }
          }
          return col
        })
        return {
          ...block,
          settings: { ...block.settings, columns: updatedColumns }
        }
      }
      return block
    }))
  }

  const hasBlocks = column.blocks && column.blocks.length > 0

  return (
    <div
      ref={setNodeRef}
      style={{
        flex: 1,
        minWidth: 0,
        padding: '0.5rem',
        border: isOver ? '2px dashed var(--color-primary)' : '2px dashed #e5e7eb',
        borderRadius: 'var(--radius-md)',
        backgroundColor: isOver ? 'rgba(99, 102, 241, 0.05)' : (hasBlocks ? '#ffffff' : '#fafafa'),
        transition: 'all 0.2s',
        minHeight: '80px'
      }}
    >
      {!hasBlocks && (
        <div style={{
          padding: '1rem 0.75rem',
          fontSize: '0.8rem',
          color: '#9ca3af',
          backgroundColor: '#f9fafb',
          borderRadius: '4px',
          textAlign: 'center',
          border: '1px dashed #e5e7eb'
        }}>
          Drop blocks here
        </div>
      )}

      {column.content && !hasBlocks && (
        <div style={{
          padding: '0.75rem',
          fontSize: '0.9rem',
          color: '#333',
          backgroundColor: '#fff',
          borderRadius: '4px',
          marginBottom: '0.5rem',
          border: '1px solid #e5e7eb'
        }}>
          {column.content}
        </div>
      )}

      {column.blocks?.map(block => {
        const isSelected = selectedBlockId === block.id
        return (
          <div
            key={block.id}
            onClick={(e) => {
              e.stopPropagation()
              setSelectedBlockId(block.id)
            }}
            style={{
              marginBottom: '0.5rem',
              border: isSelected ? '2px solid var(--color-primary)' : '2px solid transparent',
              borderRadius: 'var(--radius-md)',
              padding: '0.25rem',
              cursor: 'pointer',
              transition: 'all 0.2s',
              position: 'relative'
            }}
          >
            {isSelected && (
              <button
                onClick={(e) => handleDeleteNestedBlock(block.id, e)}
                title="Delete"
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  width: '22px',
                  height: '22px',
                  borderRadius: '50%',
                  background: '#dc2626',
                  color: '#fff',
                  border: '2px solid #fff',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 5,
                  boxShadow: '0 2px 4px rgba(0,0,0,0.15)'
                }}
              >
                <Trash2 size={11} />
              </button>
            )}
            <BlockRenderer block={block} />
          </div>
        )
      })}

      {/* Drop indicator at bottom when hovering with blocks already present */}
      {isOver && hasBlocks && (
        <div style={{
          height: '3px',
          backgroundColor: 'var(--color-primary)',
          borderRadius: '2px',
          marginTop: '0.25rem',
          boxShadow: '0 0 6px rgba(99, 102, 241, 0.4)'
        }} />
      )}
    </div>
  )
}

export default function ColumnBlock({ settings, blockId }) {
  const { padding, margin, backgroundColor, columnCount, gap, columns } = settings

  const containerStyle = {
    padding: `${padding.top} ${padding.right} ${padding.bottom} ${padding.left}`,
    margin: `${margin.top} ${margin.right} ${margin.bottom} ${margin.left}`,
    backgroundColor: backgroundColor || 'transparent',
    display: 'flex',
    gap: gap || '10px'
  }

  return (
    <div style={containerStyle}>
      {Array.from({ length: columnCount }).map((_, idx) => {
        const column = columns[idx] || { blocks: [], content: '' }
        const columnId = `${blockId}-col-${idx}`

        return (
          <DroppableColumn
            key={columnId}
            columnId={columnId}
            column={column}
            columnIndex={idx}
            parentBlockId={blockId}
          />
        )
      })}
    </div>
  )
}
